import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBuildPageHeader,
  MiIcon,
  MiList,
  MiLoader,
  MiMenu,
  MiStackedTypography,
} from "@miplatform/components";
import {
  ORDER_STATUS_DISPLAY,
  ORDER_STATUS_TITLE_COLORS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  getHomeOrderCardStyles,
  getNestedValue,
  getWorkOrderTabStats,
  parseFieldValue,
} from "@miplatform/utils";
import { mdiFileDocumentOutline, mdiFormatListBulleted } from "@mdi/js";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { Box, Divider, Typography } from "@mui/material";
import { OrderAlerts } from "../../Orders/OrdersList/OrderAlerts/OrderAlerts";
import { OrderInfoPopper } from "../../Orders/OrdersList/OrderInfoPopper/OrderInfoPopper";
import { ORDERS_LIST_CONFIG } from "../../Orders/OrdersList/orderListConfig";
import { OrdersProvider } from "../../Orders/OrdersProvider";
import { ORDERS_DEFAULT_FIELD_WIDTH } from "../../Orders/ordersConstants";
import { orderCardConfig, orderHoverCardConfig } from "./orderCardConfig";
import { useHomeOrdersTabViewModel } from "./useHomeOrdersTabViewModel";

const OrderListSection = ({ data, extraTabProps }) => {
  const { classes, theme } = useStyles();

  const [search, setSearch] = useState("");

  const { isLoading, setSelectedOrder, handleOrderActivityDrawerOpen } =
    extraTabProps;

  const sections = [
    {
      fieldsetStyle: {
        justifyContent: "flex-end",
        alignItems: "center",
      },
      fieldset: [
        {
          maxWidth: 400,
          justifyContent: "flex-end",
          renderCell: (row) => {
            const { icon, color, count } = getWorkOrderTabStats(
              theme,
              row?.workOrders
            );

            return (
              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="flex-end"
                gap={theme.spacing(2)}
              >
                <MiStackedTypography
                  title="Work Orders"
                  descriptionProps={{
                    minWidth: ORDERS_DEFAULT_FIELD_WIDTH,
                    children: (
                      <Box display="flex" alignItems="center" gap={1}>
                        <MiIcon
                          path={icon}
                          size={0.7}
                          color={color}
                          style={{
                            marginBottom: theme.spacing(0.5),
                          }}
                        />
                        {count}
                      </Box>
                    ),
                  }}
                />
                {orderCardConfig?.map(({ label, fieldKey }) => {
                  const fieldValue = parseFieldValue(
                    getNestedValue(row, fieldKey),
                    label.toLowerCase().includes("date")
                      ? "--/--/--"
                      : undefined,
                    undefined,
                    { isCurrency: fieldKey === "variance", currency: "USD" }
                  );

                  const fieldColor =
                    fieldKey === "completeDate" && fieldValue !== "--/--/--"
                      ? theme.palette.success.main
                      : theme.palette.black.main;

                  return (
                    <MiStackedTypography
                      key={fieldKey}
                      title={label}
                      titleProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
                      description={fieldValue}
                      descriptionProps={{
                        width: ORDERS_DEFAULT_FIELD_WIDTH,
                        color: fieldColor,
                      }}
                    />
                  );
                })}
              </Box>
            );
          },
        },
      ],
    },
  ];

  const handleSearch = (value) => {
    setSearch(value);
    setSelectedOrder(null);
  };

  const filterBySearch = (orders, searchTerm) => {
    if (!orders) return [];
    const lowercasedSearchTerm = searchTerm.toLowerCase();

    const filteredOrders = orders.filter((order) => {
      return (
        order.orderTemplateName?.toLowerCase().includes(lowercasedSearchTerm) ||
        order.orderTemplateTradeName
          ?.toLowerCase()
          .includes(lowercasedSearchTerm) ||
        order.orderNumber?.toString().includes(lowercasedSearchTerm) ||
        ORDER_STATUS_DISPLAY[order.orderStatus]
          ?.toLowerCase()
          .includes(lowercasedSearchTerm)
      );
    });

    return filteredOrders.map((order) => {
      return {
        ...order,
        containerStyles: getHomeOrderCardStyles(theme, order?.orderStatus),
      };
    });
  };

  return (
    <>
      <MiBuildPageHeader
        style={{ marginTop: -20 }}
        title="Orders"
        hasDivider={false}
        titleFontSize={theme.fontSize.xlarge}
        search={search}
        searchPlaceholder="Find Order"
        onHandleSearch={(e) => handleSearch(e.target.value)}
      />

      <Box className={classes.contentContainer}>
        {isLoading ? (
          <MiLoader
            isLoading={isLoading}
            style={{ position: "absolute", top: "50%" }}
          />
        ) : (
          <MiList
            viewType="card"
            data={filterBySearch(data, search) || []}
            cardProps={{
              sections,
              isAlert: ORDERS_LIST_CONFIG.All?.isAlert,
              renderTitle: (order) => (
                <Box display="flex" alignItems="center">
                  <Box display="flex" flexDirection="column" width={80}>
                    <Typography
                      fontSize={theme.fontSize.small}
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      color={theme.palette.grey.main}
                    >
                      Status
                    </Typography>
                    <Typography
                      fontSize={theme.fontSize.medium}
                      fontWeight={theme.fontWeight.medium}
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      color={ORDER_STATUS_TITLE_COLORS[order.orderStatus]}
                    >
                      {ORDER_STATUS_DISPLAY[order.orderStatus]}
                    </Typography>
                  </Box>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      mx: theme.spacing(2),
                    }}
                  />
                  <Box display="flex" flexDirection="column">
                    <Box
                      display="flex"
                      gap={theme.spacing(1)}
                      alignItems="center"
                    >
                      <Typography
                        color="primary.main"
                        fontWeight={theme.fontWeight.medium}
                        fontSize={theme.fontSize.medium}
                      >
                        {order.orderTemplateName}
                      </Typography>
                      <OrderInfoPopper
                        order={order}
                        fieldsConfig={orderHoverCardConfig}
                      />
                      {ORDERS_LIST_CONFIG.All?.getAlerts && (
                        <OrderAlerts
                          alerts={ORDERS_LIST_CONFIG.All?.getAlerts(order)}
                        />
                      )}
                    </Box>
                    <Typography
                      fontSize={theme.fontSize.small}
                      width={180}
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      color={theme.palette.grey.main}
                    >
                      {order.orderTemplateTradeName || "--"}
                    </Typography>
                  </Box>
                </Box>
              ),
              renderItemActions: (order) => {
                return (
                  <MiMenu
                    data={[
                      {
                        label: "Details",
                        icon: mdiFormatListBulleted,
                        color: theme.palette.grey.main,
                        action: () => {
                          setSelectedOrder(order);
                          handleOrderActivityDrawerOpen();
                        },
                      },
                    ]}
                  />
                );
              },
            }}
            loading={isLoading}
            emptyListProps={{
              icon: mdiFileDocumentOutline,
              text: "No Orders found",
              helperText: !search
                ? "Get started by creating a new Order for this home"
                : "Please adjust your search criteria and try again",
            }}
            hideListPagination
          />
        )}
      </Box>
    </>
  );
};

export const HomeOrdersTab = ({
  orders,
  milestones,
  setSelectedOrder,
  handleOrderActivityDrawerOpen,
  isLoading,
}) => {
  const { classes } = useStyles();
  const { query } = useRouter();

  const { ordersWithMilestones } = useHomeOrdersTabViewModel({
    orders,
    milestones,
  });

  const tabs = useMemo(() => {
    const generatedTabs = milestones?.map((item) => {
      const filteredOrders = ordersWithMilestones.reduce((acc, order) => {
        const hasMatchingMilestone = order?.milestones?.some(
          (ms) => ms.id === item.id
        );
        const alreadyExists = acc.some(
          (existingOrder) => existingOrder.id === order.id
        );

        if (hasMatchingMilestone && !alreadyExists) {
          acc.push(order);
        }

        return acc;
      }, []);

      return {
        label: item.displayName,
        value: item.id,
        data: filteredOrders,
        disabled: filteredOrders.length === 0,
        viewComponent: OrderListSection,
        extraTabProps: {
          isLoading,
          setSelectedOrder,
          handleOrderActivityDrawerOpen,
        },
      };
    });

    generatedTabs.sort((a, b) => a.label.localeCompare(b.label));

    generatedTabs.unshift({
      label: "All",
      value: "all",
      data: orders,
      viewComponent: OrderListSection,
      extraTabProps: {
        isLoading,
        setSelectedOrder,
        handleOrderActivityDrawerOpen,
      },
    });

    return generatedTabs;
  }, [orders, milestones, ordersWithMilestones, isLoading]);

  useEffect(() => {
    if (orders?.length && query.orderId) {
      const order = orders.find((item) => item.id === query.orderId);

      if (order) {
        setSelectedOrder(order);
        handleOrderActivityDrawerOpen();
      }
    }
  }, [query.orderId, orders]);

  return (
    <OrdersProvider>
      <Box className={classes.root}>
        <VerticalTabsLayout
          data={ordersWithMilestones}
          tabs={tabs}
          useLegacyStyles
          hideStatusIcons
          hideNavigationButtons
        />
      </Box>
    </OrdersProvider>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.grey.lightAccent,
    overflow: "hidden",
  },
  contentContainer: {
    flex: "1",
    overflowY: "auto",
    position: "relative",
  },
}));
